interface RateLimitEntry {
  attempts: number
  firstAttempt: number
  lastAttempt: number
  blockedUntil?: number
}

class RateLimiter {
  private storage: Map<string, RateLimitEntry> = new Map()
  private readonly maxAttempts: number
  private readonly windowMs: number
  private readonly blockDurationMs: number

  constructor(
    maxAttempts: number = 5,
    windowMs: number = 15 * 60 * 1000, // 15 minutes
    blockDurationMs: number = 15 * 60 * 1000, // 15 minutes block
  ) {
    this.maxAttempts = maxAttempts
    this.windowMs = windowMs
    this.blockDurationMs = blockDurationMs

    // Clean up old entries every 5 minutes
    setInterval(() => this.cleanup(), 5 * 60 * 1000)
  }

  isBlocked(identifier: string): boolean {
    const entry = this.storage.get(identifier)
    if (!entry) return false

    const now = Date.now()

    // Check if still blocked
    if (entry.blockedUntil && now < entry.blockedUntil) {
      return true
    }

    // Clear block if expired
    if (entry.blockedUntil && now >= entry.blockedUntil) {
      entry.blockedUntil = undefined
      entry.attempts = 0
    }

    return false
  }

  recordAttempt(
    identifier: string,
    isSuccess: boolean = false,
  ): {
    blocked: boolean
    remainingAttempts: number
    resetTime: number
  } {
    const now = Date.now()
    let entry = this.storage.get(identifier)

    if (!entry) {
      entry = {
        attempts: 0,
        firstAttempt: now,
        lastAttempt: now,
      }
      this.storage.set(identifier, entry)
    }

    // Reset if window has passed
    if (now - entry.firstAttempt > this.windowMs) {
      entry.attempts = 0
      entry.firstAttempt = now
      entry.blockedUntil = undefined
    }

    // If successful, reset attempts
    if (isSuccess) {
      entry.attempts = 0
      entry.firstAttempt = now
      entry.blockedUntil = undefined
      return {
        blocked: false,
        remainingAttempts: this.maxAttempts,
        resetTime: now + this.windowMs,
      }
    }

    // Record failed attempt
    entry.attempts++
    entry.lastAttempt = now

    // Block if too many attempts
    if (entry.attempts >= this.maxAttempts) {
      entry.blockedUntil = now + this.blockDurationMs
    }

    return {
      blocked: entry.attempts >= this.maxAttempts,
      remainingAttempts: Math.max(0, this.maxAttempts - entry.attempts),
      resetTime: entry.firstAttempt + this.windowMs,
    }
  }

  getTimeUntilReset(identifier: string): number {
    const entry = this.storage.get(identifier)
    if (!entry) return 0

    if (entry.blockedUntil) {
      return Math.max(0, entry.blockedUntil - Date.now())
    }

    return Math.max(0, entry.firstAttempt + this.windowMs - Date.now())
  }

  private cleanup(): void {
    const now = Date.now()
    for (const [key, entry] of this.storage.entries()) {
      // Remove entries older than the window and not blocked
      if (
        now - entry.lastAttempt > this.windowMs &&
        (!entry.blockedUntil || now > entry.blockedUntil)
      ) {
        this.storage.delete(key)
      }
    }
  }
}

// Create singleton instance for login rate limiting
export const loginRateLimiter = new RateLimiter(
  5, // max 5 attempts
  15 * 60 * 1000, // in 15 minutes
  15 * 60 * 1000, // block for 15 minutes
)

export const formatTimeRemaining = (milliseconds: number): string => {
  const minutes = Math.ceil(milliseconds / (1000 * 60))
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`
  }
  const hours = Math.ceil(minutes / 60)
  return `${hours} hour${hours !== 1 ? 's' : ''}`
}
